import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { BLACK2, BORDER_GRAY, WHITE } from '../../../theme';
import { EventNames } from '../../../utils/trackingEvents';
import { Accordion } from '../Accordion';
import Text, { TextColor, TextKind } from '../Text';
import { SizeProp } from '../util';

export const StyledRadioGroup = styled.div<{ $rowDisplay?: boolean }>`
  display: flex;
  flex-direction: ${({ $rowDisplay }) => ($rowDisplay ? ' row' : 'column')};
  gap: 16px;
`;
const StyledRadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StyledRadio = styled.input<{ $size: SizeProp }>`
  appearance: none;
  ${({ $size }) => {
    switch ($size) {
      case SizeProp.Small: {
        return `
          width: 16px;
          height: 16px;
        `;
      }
      case SizeProp.Medium:
      default: {
        return `
          width: 24px;
          height: 24px;
        `;
      }
    }
  }}
  background: ${WHITE};
  border: 1px solid ${BORDER_GRAY};
  border-radius: 12px;
  margin: 0;

  &:focus {
    border-color: ${BLACK2};
  }
`;

interface RadioOptionType {
  label: string;
  value: any;
  name?: string;
}

interface RadioProps extends RadioOptionType {
  checked: boolean;
  onChange: (value: RadioOptionType) => void;
  size?: SizeProp;
  trackingData?: TrackingEventData;
}

export const Radio = ({
  label,
  checked,
  onChange,
  name,
  value,
  size = SizeProp.Medium,
  trackingData,
}: RadioProps) => {
  const { track } = useTracking();

  const handleChange = () => {
    if (trackingData) {
      track(EventNames.FilterClick, {
        ...trackingData,
        event_label: label,
      });
    }
    onChange({ name, value, label });
  };

  return (
    <StyledRadioLabel>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {' '}
        <StyledRadio
          data-testid="radio-button"
          name={name}
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          $size={size}
        />{' '}
        {checked && (
          <FontAwesomeIcon
            aria-hidden
            icon={faCircle}
            color={BLACK2}
            fontSize={
              {
                small: '12px',
                medium: '16px',
              }[size]
            }
            style={{
              position: 'absolute',
            }}
          />
        )}
      </div>
      <Text
        kind={
          {
            [SizeProp.Small]: TextKind.BodyS,
            [SizeProp.Medium]: TextKind.BodyM,
          }[size]
        }
        color={TextColor.Secondary}
      >
        {label}
      </Text>
    </StyledRadioLabel>
  );
};

interface RadioGroupProps {
  label?: string;
  options: RadioOptionType[];
  name: string;
  onChange: (value: RadioOptionType) => void;
  checkedOption: RadioOptionType['value'] | undefined;
  rowDisplay?: boolean;
  collapsible?: boolean;
  size?: SizeProp;
  trackingData?: TrackingEventData;
}

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  options,
  name,
  onChange,
  checkedOption,
  rowDisplay,
  collapsible,
  size = SizeProp.Medium,
  trackingData,
}) => {
  const radioList = (
    <StyledRadioGroup $rowDisplay={rowDisplay}>
      {options.map((option) => (
        <Radio
          key={option.label}
          name={name}
          label={option.label}
          value={option.value}
          onChange={onChange}
          checked={
            JSON.stringify(checkedOption) === JSON.stringify(option.value)
          }
          size={size}
          trackingData={
            trackingData ? { ...trackingData, section_name: label } : undefined
          }
        />
      ))}
    </StyledRadioGroup>
  );

  if (collapsible) {
    return (
      <Accordion
        size={SizeProp.Small}
        name={name}
        label={
          <Text kind={TextKind.ButtonM}>
            {label} {checkedOption ? `(1)` : null}
          </Text>
        }
        trackingData={{
          ...trackingData,
          section_name: label,
        }}
      >
        <div style={{ padding: '8px' }}>{radioList}</div>
      </Accordion>
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      data-testid="checkbox-group"
    >
      {label && <Text kind={TextKind.LabelL}>{label}</Text>}
      {radioList}
    </div>
  );
};
